/**
 * Loading graphics assets such as textures and 3D models from files over HTTP.
 * @module
 */
import {
  fetchJsonAsset,
  getAssetFetcherConfig,
  processURL as baseProcessURL,
  readAssetFromFilesystem,
} from "common/utils/assetFetcher";
import { isOnNodeJS } from "common/utils/context";
import { createPerfMarker } from "common/utils/perfMetricsRecorder";
import type { BufferGeometry, PixelFormat, Texture } from "three";
import {
  Cache,
  CubeTexture,
  CubeTextureLoader,
  CubeReflectionMapping,
  RGBAFormat,
  SRGBColorSpace,
  Object3D,
} from "three";
import { createBrowserLoaderContext } from "./browserLoader";
import { ImageFormatFeature, browserCheckImageFormatSupport } from "./imageFormatSupport";
import type { ThreeAssetLoaderContext } from "./THREEAssetLoaderTypes";
import { EXRLoader } from "./threejs/examples/jsm/loaders/EXRLoader";
import type { GLTF } from "./threejs/examples/jsm/loaders/GLTFLoader";
import { GLTFLoader } from "./threejs/examples/jsm/loaders/GLTFLoader";
import { ExtendedTextureLoader } from "./threejs/higharc/ExtendedTextureLoader";

export const THREEAssetLoaderConfig = {
  enableCache: true,
  addRefreshQueryParam: true,
};

Cache.enabled = true;

export default class THREEAssetLoader {
  readonly gltfLoader = new GLTFLoader();
  private readonly cubeTextureLoader = new CubeTextureLoader();
  private readonly exrLoader = new EXRLoader();
  private readonly textureCache: { [key: string]: Promise<Texture> } = {};
  private readonly cubemapCache: { [key: string]: Promise<CubeTexture> } = {};
  private readonly dracoCache: { [key: string]: Promise<BufferGeometry> } = {};
  private readonly gltfCache: { [key: string]: Promise<Object3D> } = {};

  private static dracoLoader: any;

  private static lossyWebPSupportedPromise: Promise<boolean>;
  static async setupLossyWebPSupported(): Promise<boolean> {
    if (this.lossyWebPSupportedPromise) {
      return this.lossyWebPSupportedPromise;
    }
    this.lossyWebPSupportedPromise = (async () => {
      await this.setup();
      let lossyWebPSupported = false;
      if (getAssetFetcherConfig().mode === "Browser") {
        lossyWebPSupported = await browserCheckImageFormatSupport(ImageFormatFeature.WEBP_LOSSY);
      }
      return lossyWebPSupported;
    })();
    return this.lossyWebPSupportedPromise;
  }

  private static avifSupportedPromise: Promise<boolean>;
  static async setupAvifSupported(): Promise<boolean> {
    if (this.avifSupportedPromise) {
      return this.avifSupportedPromise;
    }
    this.avifSupportedPromise = (async () => {
      await this.setup();
      let avifSupported = false;
      if (getAssetFetcherConfig().mode === "Browser") {
        avifSupported = await browserCheckImageFormatSupport(ImageFormatFeature.AVIF);
      }
      return avifSupported;
    })();
    return this.avifSupportedPromise;
  }

  private static setupPromise: Promise<void> | undefined;

  /**
   * Normally this is called automatically when the asset loader is used, but you can call this manually to ensure that THREEAssetLoader setup is complete.
   */
  static setup(): Promise<void> {
    if (THREEAssetLoader.setupPromise) {
      return THREEAssetLoader.setupPromise;
    }
    THREEAssetLoader.setupPromise = (async () => {
      if (isOnNodeJS()) {
        const { setupNodeThreeAssetLoader } = await import("./nodeLoader");
        const context = await setupNodeThreeAssetLoader();
        THREEAssetLoader.setTHREEAssetLoaderContext(context);
      } else {
        const context = createBrowserLoaderContext();
        THREEAssetLoader.setTHREEAssetLoaderContext(context);
      }
    })();
    return THREEAssetLoader.setupPromise;
  }

  private static _instance: THREEAssetLoader | undefined;

  static get instance(): THREEAssetLoader {
    return this._instance ?? (this._instance = new THREEAssetLoader());
  }

  static setTHREEAssetLoaderContext({ dracoLoader }: ThreeAssetLoaderContext) {
    THREEAssetLoader.dracoLoader = dracoLoader;
  }

  private constructor() {}

  private static processURL(url: string, forceRefresh = false) {
    return baseProcessURL(url, THREEAssetLoaderConfig.addRefreshQueryParam || forceRefresh);
  }

  async loadCubeTexture(urls: string[]): Promise<CubeTexture> {
    await THREEAssetLoader.setup();

    // NodeLocal: CubeTextureLoader doesn't work in Node.js, use canvas loadImage
    if (getAssetFetcherConfig().mode === "NodeLocal") {
      const key = urls[0];
      if (THREEAssetLoaderConfig.enableCache && this.cubemapCache.hasOwnProperty(key)) {
        return this.cubemapCache[key];
      }

      return (this.cubemapCache[key] = (async () => {
        const { loadImage } = await import("canvas");
        const path = await import("path");

        const images = await Promise.all(
          urls.map(async (url) => {
            const filePath = path.join("apps/web/public", url);
            return loadImage(filePath);
          }),
        );

        const cubeTexture = new CubeTexture(images as unknown as HTMLImageElement[]);
        cubeTexture.mapping = CubeReflectionMapping;
        cubeTexture.format = RGBAFormat;
        cubeTexture.colorSpace = SRGBColorSpace;
        cubeTexture.needsUpdate = true;
        return cubeTexture;
      })());
    }

    // Browser: use CubeTextureLoader
    urls = urls.map((url) => THREEAssetLoader.processURL(url));

    const key = urls[0];
    if (THREEAssetLoaderConfig.enableCache && this.cubemapCache.hasOwnProperty(key)) {
      return this.cubemapCache[key];
    }

    return (this.cubemapCache[key] = new Promise((resolve, reject) => {
      this.cubeTextureLoader.load(urls, resolve, undefined, reject);
    }));
  }

  async loadTexture(url: string, isEXR?: boolean, overrideFormat?: PixelFormat): Promise<Texture> {
    await THREEAssetLoader.setup();
    isEXR ??= url.endsWith(".exr");
    if (THREEAssetLoaderConfig.addRefreshQueryParam && !url.startsWith("data:")) {
      url = THREEAssetLoader.processURL(url);
    }

    if (THREEAssetLoaderConfig.enableCache && this.textureCache.hasOwnProperty(url)) {
      return this.textureCache[url];
    }

    return (this.textureCache[url] = new Promise((resolve, reject) => {
      if (isEXR) {
        this.exrLoader.load(url, resolve, undefined, reject);
      } else {
        ExtendedTextureLoader.instance
          .loadTexture({ url, onLoad: resolve, onError: reject, overrideFormat })
          .catch(reject);
      }
    }));
  }

  async loadTextureDirect({
    url,
    onLoad,
    onError,
    overrideFormat,
    texture,
  }: {
    url: string;
    onLoad?: (texture: Texture) => void;
    onError?: (error: ErrorEvent) => void;
    overrideFormat?: PixelFormat;
    texture: Texture; // The texture that's passed in will be overwritten by the texture loader.
  }): Promise<Texture> {
    await THREEAssetLoader.setup();
    return ExtendedTextureLoader.instance.loadTexture({
      url: THREEAssetLoader.processURL(url),
      onLoad,
      onError,
      overrideFormat,
      texture,
    });
  }

  async loadDRACO(url: string): Promise<BufferGeometry> {
    await THREEAssetLoader.setup();
    const config = getAssetFetcherConfig();

    // For NodeLocal, convert URL to filesystem path
    let cacheKey: string;
    let filePath: string | null = null;

    if (config.mode === "NodeLocal") {
      // Convert /static/... URL to apps/web/public/static/... path
      filePath = "apps/web/public" + url;
      cacheKey = filePath;
    } else {
      url = THREEAssetLoader.processURL(url);
      cacheKey = url;
    }

    if (THREEAssetLoaderConfig.enableCache && this.dracoCache.hasOwnProperty(cacheKey)) {
      return this.dracoCache[cacheKey];
    }

    return (this.dracoCache[cacheKey] = (async () => {
      if (!THREEAssetLoader.dracoLoader) {
        throw new Error("Setup the draco loader!");
      }
      const marker = createPerfMarker(`loadDRACO-${cacheKey}`);

      // For NodeLocal, read file content before entering the Promise
      let arrayBuffer: ArrayBuffer | null = null;
      if (config.mode === "NodeLocal" && filePath) {
        const fileContent = await readAssetFromFilesystem(filePath);
        // Node.js Buffer pooling means small files may share an ArrayBuffer with garbage data.
        // Slice to get only the actual file bytes.
        arrayBuffer = fileContent.buffer.slice(fileContent.byteOffset, fileContent.byteOffset + fileContent.byteLength);
      }

      return new Promise<BufferGeometry>((resolve, reject) => {
        const onLoad = (x: BufferGeometry): void => {
          if ((x.attributes.position as any).isQuantized) {
            reject(new Error("Position attribute is quantized"));
            return;
          }

          if (x.attributes.normal === undefined) {
            reject(new Error("No normals provided"));
            return;
          }

          x.computeBoundingBox();
          marker.endAndMeasure();
          resolve(x);
        };

        if (arrayBuffer !== null) {
          // NodeLocal: parse from filesystem content
          THREEAssetLoader.dracoLoader.parse(arrayBuffer, onLoad, reject);
        } else {
          // Browser: use URL-based loading
          THREEAssetLoader.dracoLoader.load(url, onLoad, undefined, reject);
        }
      });
    })());
  }

  static processGLTFScene(scene: Object3D): Object3D {
    if (!(scene instanceof Object3D)) {
      throw new Error("Expected scene node of gltf to be a Object3D");
    }

    const prop = scene.children[0];

    if (!prop) {
      throw new Error("Expected first child of gltf to exist");
    }

    // Sanity check that we're not throwing away transforms.
    scene.updateMatrix();
    const sceneMatrix = scene.matrix.elements;
    for (let i = 0; i < 16; i++) {
      const expectedValue = i % 5 === 0 ? 1 : 0;
      if (Math.abs(sceneMatrix[i] - expectedValue) > 1e-4) {
        throw new Error("Expected the scene that acts as the gltf prop container to have identity transform");
      }
    }

    prop.removeFromParent();

    return prop;
  }

  async parseGLTF(bf: ArrayBuffer): Promise<GLTF> {
    await THREEAssetLoader.setup();
    this.gltfLoader.setDRACOLoader(THREEAssetLoader.dracoLoader);

    return new Promise((res, rej) => {
      this.gltfLoader.parse(
        bf,
        "",
        (g) => res(g),
        (e) => rej(e),
      );
    });
  }

  async loadGLTF(sourceUrl: string, prefix?: string | null): Promise<Object3D> {
    const marker = createPerfMarker(`loadGLTF-${sourceUrl}`);
    await THREEAssetLoader.setup();
    this.gltfLoader.setDRACOLoader(THREEAssetLoader.dracoLoader);
    const config = getAssetFetcherConfig();

    if (prefix === undefined) {
      if (config.mode === "Browser") {
        prefix = "/static/props/gltf/";
      } else {
        prefix = "apps/web/public/static/props/gltf/";
      }
    }

    const url = prefix === null ? sourceUrl : THREEAssetLoader.processURL(prefix + sourceUrl);

    if (THREEAssetLoaderConfig.enableCache && this.gltfCache.hasOwnProperty(url)) {
      return this.gltfCache[url];
    }

    return (this.gltfCache[url] = (async () => {
      // For non-Browser modes, fetch the data before entering the callback-based Promise
      let arrayBuffer: ArrayBuffer | null = null;
      if (config.mode === "NodeLocal") {
        const fileContent = await readAssetFromFilesystem((prefix ?? "") + sourceUrl, "binary");
        const view = new Uint8Array(fileContent.length);
        for (let i = 0; i < fileContent.length; i++) {
          view[i] = fileContent.charCodeAt(i);
        }
        arrayBuffer = view.buffer;
      }

      return new Promise<Object3D>((resolve, reject) => {
        if (config.mode === "Browser") {
          this.gltfLoader.load(
            url,
            ({ scene }) => {
              resolve(THREEAssetLoader.processGLTFScene(scene));
              marker.endAndMeasure();
            },
            undefined,
            reject,
          );
        } else if (arrayBuffer !== null) {
          this.gltfLoader.parse(
            arrayBuffer,
            "",
            ({ scene }) => {
              resolve(THREEAssetLoader.processGLTFScene(scene));
              marker.endAndMeasure();
            },
            reject,
          );
        }
      });
    })());
  }

  /**
   * Fetch JSON content from a URL.
   * Delegates to fetchJsonAsset from common/utils/assetFetcher.
   */
  async fetchJson(url: string, forceRefresh: boolean = false): Promise<string | undefined> {
    await THREEAssetLoader.setup();
    return fetchJsonAsset(url, forceRefresh);
  }
}

// Call setup here so that users can await on only webpSupportInitialized or avifSupportInitialized without having to await setup.
void THREEAssetLoader.setup();
